import type { JSX, ReactNode } from 'react';

import { Color } from '@/constants/color';
import { IntakeStage, IntakeStageValue } from '@/hooks/useIntakeProgress';
import Badge from '@/ui/components/Badge/Badge';
import Tooltip from '@/ui/components/Tooltip/Tooltip';

interface IntakeStatusBadgeProps {
  stage: IntakeStageValue;
  eligibilityReason: string | null;
  ineligibilityReasons?: string[];
}

const STAGE_BADGE_CONFIG: Record<IntakeStageValue, { text: string; color: Color }> = {
  [IntakeStage.PSI]: { text: 'Intake scheduled', color: Color.BLUE },
  [IntakeStage.SSI]: { text: 'SSI Eligible', color: Color.GREEN },
  [IntakeStage.NOT_SSI_ELIGIBLE]: { text: 'SSI Ineligible', color: Color.RED }
};

const IntakeStatusBadge = ({
  stage,
  eligibilityReason,
  ineligibilityReasons
}: IntakeStatusBadgeProps): JSX.Element => {
  const { text, color } = STAGE_BADGE_CONFIG[stage];
  const badge = <Badge color={color} text={text} fontSize="text-sm" />;

  let tooltipContent: ReactNode = null;
  if (
    stage === IntakeStage.NOT_SSI_ELIGIBLE &&
    ineligibilityReasons &&
    ineligibilityReasons.length > 0
  ) {
    tooltipContent = (
      <div>
        <div className="font-semibold mb-2">Reasons for ineligibility:</div>
        <ul className="list-disc list-inside space-y-1">
          {ineligibilityReasons.map((reason, index) => (
            <li key={index} className="text-sm">
              {reason}
            </li>
          ))}
        </ul>
      </div>
    );
  } else if (eligibilityReason) {
    tooltipContent = <div className="text-sm">{eligibilityReason}</div>;
  }

  if (!tooltipContent) {
    return badge;
  }

  return (
    <Tooltip tooltipContent={tooltipContent} placement="top">
      {badge}
    </Tooltip>
  );
};

export default IntakeStatusBadge;
